<template>
  <DashboardContainer>
    <GivarooSpinner v-if="loading" class="mt-24" />
    <template v-else>
      <div class="campaign-data-shell">
        <section class="campaign-data-toolbar">
          <div class="flex flex-wrap items-center gap-2">
            <BaseButton
              v-if="canManage"
              variant="primary"
              :icon="PlusIcon"
              @click="openUploadModal"
            >
              Upload document
            </BaseButton>
          </div>
          <p class="text-sm text-gray-500 sm:max-w-xs sm:text-right">
            <span class="font-medium text-gray-700">{{ docs.length }}</span>
            {{ docs.length === 1 ? 'document' : 'documents' }}
          </p>
        </section>

        <div
          v-if="!docs.length"
          class="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center sm:p-10"
        >
          <p class="text-sm text-gray-600">No documents in the library yet.</p>
          <p v-if="canManage" class="mt-1 text-sm text-gray-500">
            Upload a PDF or Word document to get started.
          </p>
        </div>

        <template v-else>
          <!-- Mobile: card list  -->
          <div class="space-y-3 md:hidden">
            <article v-for="doc in docs" :key="`mobile-${doc.id}`" class="campaign-data-card">
              <div class="campaign-data-card__header">
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-semibold text-gray-900">{{ doc.title }}</p>
                </div>
                <button
                  v-if="canManage"
                  type="button"
                  class="inline-flex shrink-0 items-center text-red-600 hover:text-red-700 disabled:opacity-50"
                  :disabled="deletingId === doc.id"
                  :aria-label="deletingId === doc.id ? 'Deleting' : `Delete ${doc.title}`"
                  @click="remove(doc)"
                >
                  <TrashIcon class="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <div class="campaign-data-card__stats">
                <div v-if="showOrgColumn" class="campaign-data-stat-row">
                  <span class="campaign-data-stat-label">Organization</span>
                  <span class="campaign-data-stat-value truncate">
                    {{ doc.canvassing_org_name || '—' }}
                  </span>
                </div>
                <div class="campaign-data-stat-row">
                  <span class="campaign-data-stat-label">Uploaded by</span>
                  <span class="campaign-data-stat-value truncate">
                    {{ doc.uploaded_by_name || '—' }}
                  </span>
                </div>
                <div class="campaign-data-stat-row">
                  <span class="campaign-data-stat-label">Date</span>
                  <span class="campaign-data-stat-value">
                    {{ formatDate(doc.datetime_created) }}
                  </span>
                </div>
              </div>
              <div class="mt-3">
                <button
                  type="button"
                  class="text-sm font-medium text-primary hover:underline disabled:opacity-50"
                  :disabled="openingId === doc.id"
                  @click="openFile(doc)"
                >
                  {{ openLabel(doc) }}
                </button>
              </div>
            </article>
          </div>

          <!-- Desktop: table -->
          <div class="hidden overflow-x-auto rounded-lg border border-gray-200 bg-white md:block">
            <table class="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="px-4 py-3 font-semibold text-gray-700">Title</th>
                  <th
                    v-if="showOrgColumn"
                    scope="col"
                    class="px-4 py-3 font-semibold text-gray-700"
                  >
                    Organization
                  </th>
                  <th scope="col" class="px-4 py-3 font-semibold text-gray-700">Uploaded by</th>
                  <th scope="col" class="px-4 py-3 font-semibold text-gray-700">Date</th>
                  <th scope="col" class="px-4 py-3 font-semibold text-gray-700">Actions</th>
                  <th v-if="canManage" scope="col" class="px-4 py-3 font-semibold text-gray-700">
                    Delete
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="doc in docs" :key="doc.id" class="hover:bg-gray-50">
                  <td class="px-4 py-3 font-medium text-gray-900">{{ doc.title }}</td>
                  <td v-if="showOrgColumn" class="px-4 py-3 text-gray-600">
                    {{ doc.canvassing_org_name || '—' }}
                  </td>
                  <td class="px-4 py-3 text-gray-600">{{ doc.uploaded_by_name || '—' }}</td>
                  <td class="px-4 py-3 text-gray-600">
                    {{ formatDate(doc.datetime_created) }}
                  </td>
                  <td class="px-4 py-3">
                    <button
                      type="button"
                      class="text-primary hover:underline disabled:opacity-50"
                      :disabled="openingId === doc.id"
                      @click="openFile(doc)"
                    >
                      {{ openLabel(doc) }}
                    </button>
                  </td>
                  <td v-if="canManage" class="px-4 py-3">
                    <button
                      type="button"
                      class="inline-flex items-center text-red-600 hover:text-red-700 disabled:opacity-50"
                      :disabled="deletingId === doc.id"
                      :aria-label="deletingId === doc.id ? 'Deleting' : `Delete ${doc.title}`"
                      @click="remove(doc)"
                    >
                      <TrashIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>

      <Modal
        :modalOpen="showUploadModal"
        headerText="Upload document"
        ctaText=""
        :showCancelButton="false"
        :hideCtaOnSuccess="true"
        @closeModal="closeUploadModal"
      >
        <form class="form-stack text-left" @submit.prevent="upload">
          <div>
            <label for="library-title" class="input--label--small">
              Title <span class="text-red-500">*</span>
            </label>
            <input
              id="library-title"
              v-model="uploadTitle"
              type="text"
              required
              class="input--text--small mt-1 w-full"
              placeholder="e.g. Company policy"
            />
          </div>
          <div v-if="uploadOrgs.length > 1">
            <label for="library-org" class="input--label--small">
              Organization <span class="text-red-500">*</span>
            </label>
            <select
              id="library-org"
              v-model="uploadOrg"
              required
              class="input--text--small mt-1 w-full"
            >
              <option value="" disabled>Select an organization</option>
              <option v-for="org in uploadOrgs" :key="org.id" :value="org.id">
                {{ org.name }}
              </option>
            </select>
          </div>
          <div>
            <label for="library-file" class="input--label--small">
              File (PDF or Word) <span class="text-red-500">*</span>
            </label>
            <input
              id="library-file"
              ref="fileInput"
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              required
              class="mt-1 block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200"
              @change="onFileChange"
            />
            <p class="mt-1 text-xs text-gray-500">PDF, DOC or DOCX, up to 25 MB.</p>
          </div>
          <div class="form-actions form-section-divider">
            <BaseButton type="button" variant="secondary" @click="closeUploadModal">
              Cancel
            </BaseButton>
            <BaseButton
              type="submit"
              variant="primary"
              :loading="uploading"
              :disabled="uploading || !uploadTitle.trim() || !uploadFile || !orgChosen"
            >
              {{ uploading ? 'Uploading…' : 'Upload' }}
            </BaseButton>
          </div>
        </form>
      </Modal>
    </template>
  </DashboardContainer>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'
import { PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'
import DashboardContainer from '@/components/DashboardContainer.vue'
import GivarooSpinner from '@/components/GivarooSpinner.vue'
import Modal from '@/components/Modal.vue'
import {
  deleteLibraryDocument,
  listLibraryDocuments,
  listLibraryOrganizations,
  openLibraryDocument,
  uploadLibraryDocument,
} from '@/services/library/api'

const store = useStore()
const toast = useToast()

const loading = ref(true)
const docs = ref([])
const showUploadModal = ref(false)
const uploadTitle = ref('')
const uploadFile = ref(null)
const fileInput = ref(null)
const uploading = ref(false)
const deletingId = ref(null)
const openingId = ref(null)
const uploadOrgs = ref([])
const uploadOrg = ref('')

const canManage = computed(
  () =>
    store.getters.isCanvassingOrgAdmin ||
    store.getters.isCanvassingOrgFieldDirector ||
    store.getters.isNPOAdmin ||
    store.getters.isGivarooStaff,
)

// Only ask which org when the user can upload to more than one (staff, super admins).
const orgChosen = computed(() => uploadOrgs.value.length <= 1 || Boolean(uploadOrg.value))

// Staff and super admins see documents from several orgs, so label each one.
const showOrgColumn = computed(() => new Set(docs.value.map((d) => d.canvassing_org)).size > 1)

function formatDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString()
}

// The server checks the file's contents too; this just catches mistakes before uploading.
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx']
const ALLOWED_MESSAGE = 'Only PDF and Word (.doc, .docx) files are allowed.'
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024

function fileExtension(name) {
  const parts = (name || '').toLowerCase().split('.')
  return parts.length > 1 ? parts.pop() : ''
}

function isAllowedFile(file) {
  return Boolean(file) && ALLOWED_EXTENSIONS.includes(fileExtension(file.name))
}

function isWordDoc(doc) {
  return doc.file_type === 'doc' || doc.file_type === 'docx'
}

function openLabel(doc) {
  if (openingId.value === doc.id) return isWordDoc(doc) ? 'Downloading…' : 'Opening…'
  return isWordDoc(doc) ? 'Download' : 'Open File'
}

async function openUploadModal() {
  uploadTitle.value = ''
  uploadFile.value = null
  uploadOrg.value = ''
  showUploadModal.value = true
  try {
    uploadOrgs.value = await listLibraryOrganizations()
  } catch (error) {
    console.error('Failed to load organizations:', error)
    uploadOrgs.value = []
  }
}

function closeUploadModal() {
  showUploadModal.value = false
  uploadTitle.value = ''
  uploadFile.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

function onFileChange(event) {
  const file = event.target.files?.[0] || null
  const problem =
    file && !isAllowedFile(file)
      ? ALLOWED_MESSAGE
      : file && file.size > MAX_UPLOAD_BYTES
      ? 'File is too large. The maximum size is 25 MB.'
      : ''
  if (problem) {
    toast.error(problem)
    uploadFile.value = null
    if (fileInput.value) {
      fileInput.value.value = ''
    }
    return
  }
  uploadFile.value = file
}

async function loadDocs() {
  loading.value = true
  try {
    const data = await listLibraryDocuments()
    docs.value = Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Failed to load library documents:', error)
    toast.error('Could not load library documents.')
    docs.value = []
  } finally {
    loading.value = false
  }
}

async function upload() {
  if (!uploadTitle.value.trim() || !uploadFile.value || !orgChosen.value) return
  if (!isAllowedFile(uploadFile.value)) {
    toast.error(ALLOWED_MESSAGE)
    return
  }
  uploading.value = true
  try {
    const created = await uploadLibraryDocument({
      title: uploadTitle.value.trim(),
      file: uploadFile.value,
      canvassingOrg: uploadOrgs.value.length > 1 ? uploadOrg.value : undefined,
    })
    docs.value = [created, ...docs.value]
    toast.success('Document uploaded.')
    closeUploadModal()
  } catch (error) {
    console.error('Failed to upload library document:', error)
    const detail =
      error.response?.data?.file?.[0] ||
      error.response?.data?.canvassing_org?.[0] ||
      error.response?.data?.detail ||
      'Could not upload document.'
    toast.error(typeof detail === 'string' ? detail : 'Could not upload document.')
  } finally {
    uploading.value = false
  }
}

async function remove(doc) {
  if (!window.confirm(`Delete "${doc.title}"?`)) return
  deletingId.value = doc.id
  try {
    await deleteLibraryDocument(doc.id)
    docs.value = docs.value.filter((item) => item.id !== doc.id)
    toast.success('Document deleted.')
  } catch (error) {
    console.error('Failed to delete library document:', error)
    toast.error('Could not delete document.')
  } finally {
    deletingId.value = null
  }
}

// Browsers can't show Word files, so save them instead of opening a tab.
async function downloadFile(doc) {
  openingId.value = doc.id
  try {
    const blob = await openLibraryDocument(doc.id)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${doc.title}.${doc.file_type}`
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 60 * 1000)
  } catch (error) {
    console.error('Failed to download library document:', error)
    toast.error('Could not download document.')
  } finally {
    openingId.value = null
  }
}

async function openFile(doc) {
  if (isWordDoc(doc)) {
    downloadFile(doc)
    return
  }
  // Open during the click gesture so the browser does not treat it as a blocked popup.
  const previewWindow = window.open('about:blank', '_blank')
  if (!previewWindow) {
    toast.error('Pop-up blocked. Allow pop-ups to open the file.')
    return
  }

  openingId.value = doc.id
  try {
    const blob = await openLibraryDocument(doc.id)
    const pdfBlob =
      blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
    const url = URL.createObjectURL(pdfBlob)
    previewWindow.location.href = url
    // Give the new tab time to load the PDF, then free the blob.
    setTimeout(() => URL.revokeObjectURL(url), 60 * 1000)
  } catch (error) {
    console.error('Failed to open library document:', error)
    previewWindow.close()
    toast.error('Could not open document.')
  } finally {
    openingId.value = null
  }
}

onMounted(loadDocs)
</script>
